import { convertDateOnly, formatCurrency } from '@rhybus/common-functions'
import { rhybusRListsHttp } from '../api/rhybus-list-http'
import { defaultSelectedExportColumns, defaultSelectedTrainingExportColumns, FilterState, KPI_QAQC_BRANCHES, KPI_STATUS_LIST, TRAINING_KPI_STATUS_LIST, ListNames, ListViewCopyTarget, ListViewExportColumn, listViewExportColumns, trainingExportColumns, RequirementItem, RequirementTabKey, RequirementTableColumn, STATUS_ENUM, HomeNewRequirementItem, TRAINING_STATUS_LABEL } from '../config/configuration'
import { dbStore, saveItems, getAllItemsInStore, getLatestModifiedDate, deleteIDB } from '@rhybus/browser-storage'
import type { I_Master } from '../interfaces/master.interface'

type ExportPreferenceItem = {
	ID?: number
	UserId?: string
	Source?: string
	SourceColumns?: string
	SelectedColumns?: string
	Columns?: string
	ColumnData?: string
	TableColumns?: string
}

const getColumnPayload = (item?: ExportPreferenceItem | null): string | null => {
	if (!item) return null

	return item.SourceColumns
		?? item.SelectedColumns
		?? item.Columns
		?? item.ColumnData
		?? item.TableColumns
		?? null
}

export const listViewCopyTargets: ListViewCopyTarget[] = [
	{ value: 'dept1', label: 'HR Department' },
	{ value: 'dept2', label: 'IT Department' },
	{ value: 'dept3', label: 'Finance Department' }
]

export const getAvailableExportColumns = (selectedColumns: ListViewExportColumn[] = defaultSelectedExportColumns) => {
	return listViewExportColumns.filter(column => !selectedColumns.some(selected => selected.fieldName === column.fieldName))
}

export const parseColumnData = (value?: string | null): ListViewExportColumn[] => {
	if (!value) return []

	try {
		const parsed = JSON.parse(value)
		if (!Array.isArray(parsed)) return []

		return parsed
			.filter((column): column is ListViewExportColumn => !!column?.fieldName && !!column?.displayName)
			.map(column => {
				const mapped: ListViewExportColumn = {
					tableName: column.tableName ?? 'Requirements',
					fieldName: column.fieldName,
					displayName: column.displayName
				}
				if (column.IsSelected !== undefined) {
					mapped.IsSelected = column.IsSelected
				}
				return mapped
			})
	} catch (error) {
		console.error('Failed to parse export column data:', error)
		return []
	}
}

export const parseTableColumnData = (value?: string | null): RequirementTableColumn[] => {
	if (!value) return []

	try {
		const parsed = JSON.parse(value)
		if (!Array.isArray(parsed)) return []

		return parsed
			.filter((column): column is RequirementTableColumn => !!(column?.key ?? column?.fieldName) && !!(column?.label ?? column?.displayName))
			.map((column: any) => ({
				key: column.key ?? column.fieldName,
				label: column.label ?? column.displayName,
				canSort: column.canSort ?? true
			}))
	} catch (error) {
		console.error('Failed to parse table column data:', error)
		return []
	}
}

export const buildDefaultExportColumns = (): ListViewExportColumn[] =>
	listViewExportColumns.map((column, index) => ({
		...column,
		IsSelected: index < defaultSelectedExportColumns.length
	}))

export const getAvailableColumnsByReference = async (source: string): Promise<ListViewExportColumn[]> => {
	try {
		const list = await rhybusRListsHttp().getList(ListNames.EXPORT_COLUMN_CONFIGURATION)
		const items = [...(await list.query().filter(`Source eq '${source}'`).get<any>())]

		const configItem = items?.[0] as ExportPreferenceItem | undefined
		return parseColumnData(getColumnPayload(configItem))
	} catch (error) {
		console.error('Failed to load export column configuration:', error)
		return []
	}
}

export const getTableColumnsByReference = async (source: string): Promise<RequirementTableColumn[]> => {
	try {
		const list = await rhybusRListsHttp().getList(ListNames.EXPORT_COLUMN_CONFIGURATION)
		const items = [...(await list.query().filter(`Source eq '${source}'`).get<any>())]

		const configItem = items?.[0] as ExportPreferenceItem | undefined
		return parseTableColumnData(getColumnPayload(configItem))
	} catch (error) {
		console.error('Failed to load table column configuration:', error)
		return []
	}
}

export const getUserPreferences = async (userId: string, source: string): Promise<ExportPreferenceItem | null> => {
	if (!userId) return null

	try {
		const list = await rhybusRListsHttp().getList(ListNames.USERPREFERENCES)
		const items = [...(await list.query().filter(`UserId eq '${userId}' and Source eq '${source}'`).get<any>())]

		return (items?.[0] as ExportPreferenceItem | undefined) ?? null
	} catch (error) {
		console.error('Failed to load user export preferences:', error)
		return null
	}
}

export const saveUserPreferences = async (
	userId: string,
	columns: ListViewExportColumn[],
	source: string,
	existingPreferenceId?: string
) => {
	const payload = {
		Title: `${source}-${userId || 'anonymous'}`,
		UserId: userId,
		Source: source,
		SelectedColumns: JSON.stringify(columns)
	}

	const list = await rhybusRListsHttp().getList(ListNames.USERPREFERENCES)
	await list.addOrUpdateItem(existingPreferenceId ? { eKey: existingPreferenceId, ...payload } : payload)
}

export const getListViewExportFileName = (tabKey: RequirementTabKey) => `Cases-${tabKey}`

export const getTrainingExportFileName = (tabKey: RequirementTabKey) => `Trainings-${tabKey}`

export const getAvailableTrainingExportColumns = (selectedColumns: ListViewExportColumn[] = defaultSelectedTrainingExportColumns) =>
	trainingExportColumns.filter(column => !selectedColumns.some(selected => selected.fieldName === column.fieldName))

export const buildDefaultTrainingExportColumns = (): ListViewExportColumn[] =>
	trainingExportColumns.map((column, index) => ({
		...column,
		IsSelected: index < defaultSelectedTrainingExportColumns.length
	}))

export const getListViewCopyPayload = (items: RequirementItem[]) => {
	return items.map(item => ({
		id: item.ItemGuid,
		title: item.requirementTitle,
		status: item.status,
		product: item.product
	}))
}

export const hasSelectedRequirements = (items?: RequirementItem[] | null) => Array.isArray(items) && items.length > 0

export const FILTER_BASE_CONFIG = [
	{ key: 'caseType', label: 'Case Type' },
	{ key: 'programtype', label: 'Program Type' },
	{ key: 'ia', label: 'IA' },
	{ key: 'country', label: 'Country' },
	{ key: 'submitteddate', label: 'Submitted Date' },
	{ key: 'fiscalYear', label: 'Fiscal Year' },
]

export const buildDynamicFilterConfig = (items: RequirementItem[]) => {
	return FILTER_BASE_CONFIG.map(filter => {
		const uniqueValues = [
			...new Set(
				items
					.map(item => String(item[filter.key as keyof RequirementItem] ?? ''))
					.filter(v => !!v && v !== 'undefined' && v !== 'null')
			)
		].sort()

		return {
			...filter,
			isMulti: false,
			defaultValue: 'All',
			options: [
				{ label: 'All', value: 'All' },
				...uniqueValues.map(v => ({ label: v, value: v }))
			]
		}
	})
}
export const buildCascadingFilterConfig = (allItems: RequirementItem[], filters: FilterState) => {
	return FILTER_BASE_CONFIG.map(filter => {
		// Compute options using items filtered by all OTHER active filters
		const otherFilters: FilterState = {}
		Object.entries(filters).forEach(([key, val]) => {
			if (key !== filter.key) otherFilters[key] = val
		})
		const relevantItems = filterRequirements(allItems, otherFilters, '')

		const uniqueValues = [
			...new Set(
				relevantItems
					.map(item => String(item[filter.key as keyof RequirementItem] ?? ''))
					.filter(v => !!v && v !== 'undefined' && v !== 'null')
			)
		].sort()

		return {
			...filter,
			isMulti: false,
			defaultValue: 'All',
			options: [
				{ label: 'All', value: 'All' },
				...uniqueValues.map(v => ({ label: v, value: v }))
			]
		}
	})
}

export const TRAINING_FILTER_BASE_CONFIG = [
	{ key: 'userCaseId', label: 'User Case ID' },
	{ key: 'line', label: 'Line' },
	{ key: 'type', label: 'Type' },
	{ key: 'wcn', label: 'WCN' },
	{ key: 'priority', label: 'Priority' },
	{ key: 'fiscalYear', label: 'Fiscal Year' },
	{ key: 'quarter', label: 'Quarter' },
	{ key: 'rank', label: 'Rank' },
	{ key: 'tpc', label: 'TPC' },
	{ key: 'submittedDate', label: 'Submitted Date' },
]

export type TrainingFilterItem = Record<string, unknown>

export const buildDynamicTrainingFilterConfig = (items: TrainingFilterItem[]) => {
	return TRAINING_FILTER_BASE_CONFIG.map(filter => {
		const uniqueValues = [
			...new Set(
				items
					.map(item => String(item[filter.key] ?? ''))
					.filter(v => !!v && v !== 'undefined' && v !== 'null')
			)
		].sort()

		return {
			...filter,
			isMulti: false,
			defaultValue: 'All',
			options: [
				{ label: 'All', value: 'All' },
				...uniqueValues.map(v => ({ label: v, value: v }))
			]
		}
	})
}

export const filterTrainingItems = (items: TrainingFilterItem[], filters: FilterState) => {
	const activeFilters = Object.entries(filters).filter(([, v]) => {
		const valueArray = v?.value ?? []
		if (!Array.isArray(valueArray) || valueArray.length === 0) return false
		if (valueArray.length === 1 && valueArray[0]?.value === 'All') return false
		return true
	})
	return items.filter(item =>
		activeFilters.every(([key, filterEntry]) => {
			const actualValues = (filterEntry.value ?? []).filter(v => v.value !== 'All')
			if (actualValues.length === 0) return true
			const fieldValue = normalizeValue(item[key])
			return actualValues.some(fv => fieldValue === normalizeValue(fv.value))
		})
	)
}

const TRAINING_SEARCH_FIELDS: (keyof TrainingFilterItem)[] = [
	'trainingTrackId', 'userCaseId', 'line', 'type', 'wcn', 'tpc', 'status'
]

export const filterTrainingRequirements = (items: TrainingFilterItem[], filters: FilterState, searchText: string) => {
	const normalizedSearch = searchText.trim().toLowerCase()
	const filtered = filterTrainingItems(items, filters)
	if (!normalizedSearch) return filtered
	return filtered.filter(item =>
		TRAINING_SEARCH_FIELDS.some(key => normalizeValue(item[key]).includes(normalizedSearch))
	)
}

export const buildTrainingCascadingFilterConfig = (allItems: TrainingFilterItem[], filters: FilterState) => {
	return TRAINING_FILTER_BASE_CONFIG.map(filter => {
		const otherFilters: FilterState = {}
		Object.entries(filters).forEach(([key, val]) => {
			if (key !== filter.key) otherFilters[key] = val
		})
		const relevantItems = filterTrainingItems(allItems, otherFilters)

		const uniqueValues = [
			...new Set(
				relevantItems
					.map(item => String(item[filter.key] ?? ''))
					.filter(v => !!v && v !== 'undefined' && v !== 'null')
			)
		].sort()

		return {
			...filter,
			isMulti: false,
			defaultValue: 'All',
			options: [
				{ label: 'All', value: 'All' },
				...uniqueValues.map(v => ({ label: v, value: v }))
			]
		}
	})
}

export const getSelectedValues = (filters: FilterState, key: string) => {
	return (filters[key]?.value ?? [])
		.map(item => item.value)
		.filter(value => value !== 'All')
}

const normalizeValue = (val: unknown): string => String(val ?? '').toLowerCase().trim()

export const getRequirementSearchFields = (item: RequirementItem) => {
	const grant = item as any
	return [
		grant.userCaseId,
		grant.caseType,
		grant.programtype,
		grant.ia,
		grant.country,
		grant.submittedBy,
		grant.submitteddate,
		grant.status,
	]
}

export const filterRequirements = (items: RequirementItem[], filters: FilterState, searchText: string) => {
	const normalizedSearch = searchText.trim().toLowerCase()

	const activeFilters = Object.entries(filters).filter(([, v]) => {
		const valueArray = v?.value ?? []
		if (!Array.isArray(valueArray) || valueArray.length === 0) return false
		if (valueArray.length === 1 && valueArray[0]?.value === 'All') return false
		return true
	})

	return items.filter(item => {
		const matchesSearch = normalizedSearch.length === 0 || getRequirementSearchFields(item)
			.some(value => normalizeValue(value).includes(normalizedSearch))

		const matchesFilters = activeFilters.every(([key, filterEntry]) => {
			const actualValues = (filterEntry.value ?? []).filter(v => v.value !== 'All')
			if (actualValues.length === 0) return true
			const fieldValue = normalizeValue(item[key as keyof RequirementItem])
			return actualValues.some(fv => fieldValue === normalizeValue(fv.value))
		})

		return matchesSearch && matchesFilters
	})
}

export const getTabRequirements = (items: RequirementItem[], tabKey: RequirementTabKey) => {
	return items.filter(item => item.tabKey === tabKey)
}

export const getRequirementTabCounts = (items: RequirementItem[]) => ({
	all: getTabRequirements(items, 'all').length,
	canceled: getTabRequirements(items, 'canceled').length,
	saved: getTabRequirements(items, 'saved').length
})

// ------------------------------------------------
// MASTERLIST DATA FETCH & MAPPING
// ------------------------------------------------

const getMasterItemTabKey = (statusCode: string): RequirementTabKey => {
	if (statusCode === STATUS_ENUM.Saved) return 'saved'
	if (statusCode === 'Canceled') return 'canceled'
	return 'all'
}

export const mapMasterItemToRequirementItem = (item: I_Master): RequirementItem => ({
	ID: item.ID ?? 0,
	ItemGuid: item.ItemGUID ?? '',
	requestID: item.RequestID ?? '',
	requirementTitle: item.RequirementTitle ?? '',
	product: item.Product ?? '',
	tbm: item.TBM ?? '',
	division: item.Division ?? '',
	branch: item.Branch ?? '',
	cor: item.COR?.Title ?? '',
	fiscalYear: item.FY ?? '',
	requestedAmount: formatCurrency(item?.RequestedAmount ?? ''),
	revisedAmount: formatCurrency(item?.RevisedAmount ?? ''),
	fundedAmount: formatCurrency(item?.FundedAmount ?? ''),
	unfundedAmount: formatCurrency(item?.UnfundedAmount ?? ''),
	submittedDate: convertDateOnly(item.ItemCreated) ?? '',
	status: item.StatusTitle || 'N/A',
	completed: item.StatusTitle === STATUS_ENUM.Completed
		? STATUS_ENUM.Completed
		: 'Not Completed',
	tabKey: getMasterItemTabKey(item.StatusCode ?? ''),
	lastAction: item.PreviousStatusTitle ?? '',
	createdById: item?.ItemCreatedBy?.ID ?? 0,
})

// const TRAINING_STATUS_LABEL: Record<number, string> = {
// 	1: 'Saved',
// 	2: 'Submitted',
// 	3: 'Canceled',
// 	4: 'Waiting for Seat Validation',
// 	5: 'Waiting for Execution',
// 	6: 'Waiting for Utilization',
// 	7: 'Waiting for Billing',
// 	8: 'Waiting for Reconciliation',
// 	9: 'Waiting for Closure',
// }

export const getTrainingTrackItems = async () => {
	try {
		// =========================================
		// GET TRAINING TRACKS
		// =========================================
		const trainingList = await rhybusRListsHttp().getList(ListNames.DSCA_TRAINING_TRACK)
		const trainingItems = [...(await trainingList.query().get<any>())]
		// =========================================
		// GET CASE MASTER
		// =========================================
		const caseList = await rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER)
		const caseItems = [...(await caseList.query().get<any>())]
		// =========================================
		// GET CASE MASTER LINE
		// =========================================
		const lineList = await rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER_LINE)
		const lineItems = [...(await lineList.query().get<any>())]
		// =========================================
		// LOOKUP MAPS
		// =========================================
		const caseMap = new Map()
		caseItems.forEach((item: any) => {
			caseMap.set(item.CaseGUID, item.UserCaseID ?? '')
		})
		const lineMap = new Map()
		lineItems.forEach((item: any) => {
			lineMap.set(item.CaseMasterLineGUID, item.UserCaseLine)
		})
		// =========================================
		// FINAL TABLE MAPPING
		// =========================================
		const mappedItems = trainingItems.map((item: any) => {
			const statusCode = Number(item.StatusCode ?? 0)
			const isSaved = statusCode === 1
			const isCanceled = statusCode === 3
			return {
				...item,
				trainingTrackId: item.TrainingTrackID ? String(item.TrainingTrackID).padStart(5, '0') : '',
				userCaseId: caseMap.get(item.CaseGUID) || '',
				line: lineMap.get(item.CaseMasterLineGUID) || '',
				type: item.TrainingTrackType || '',
				wcn: item.WorksheetNumber || '',
				priority: item.Priority || '',
				quarter: item.Quarter || '',
				rank: item.Rank || '',
				tpc: item.TrainingPersonEmail || '',
				submittedDate: item.ItemCreated ? new Date(item.ItemCreated).toLocaleDateString() : '',
				status: TRAINING_STATUS_LABEL[statusCode] ?? 'Waiting for Seat Validation',
				trainingTrackGUID: item.TrainingTrackGUID,
				ItemGUID: item.TrainingTrackGUID,
				tabKey: isSaved ? 'saved' : isCanceled ? 'canceled' : 'all',
				fiscalYear: item.FiscalYear || '',
				trainingPersonFirstName: item.TrainingPersonFName || '',
				trainingPersonMiddleName: item.TrainingPersonMName || '',
				trainingPersonLastName: item.TrainingPersonLName || '',
				vetted: item.Vetted ? 'Yes' : 'No',
				description: item.Description || '',
			}
		})
		// Sort by ItemCreated in descending order (latest first)
		mappedItems.sort((a: any, b: any) => new Date(b.ItemCreated).valueOf() - new Date(a.ItemCreated).valueOf())
		return mappedItems
	} catch (error) {
		console.error('Failed to load training tracks:', error)
		return []
	}
}
export const getTrainingTrackLinesByGUID = async (trainingTrackGUID: string) => {
	try {
		const lineList = await rhybusRListsHttp().getList(ListNames.DSCA_TRAINING_TRACK_LINE)
		const items = [...(await lineList.query().get<any>())]
		const formatTrainingDate = (value: any) => {
			if (!value) return ''
			const date = new Date(value)
			if (isNaN(date.getTime())) return String(value)
			return date.toLocaleDateString()
		}
		return items
			.filter((item: any) => item.TrainingTrackGUID === trainingTrackGUID && !item.IsDeleted)
			.sort((a: any, b: any) => (Number(a.TrainingItemID) || 0) - (Number(b.TrainingItemID) || 0))
			.map((item: any) => ({
				id: item.eKey,
				eTag: item.eTag,
				training: item.TrainingItemID || '',
				suffix: item.WCNSuffix || '',
				location: item.Location || '',
				exa: item.EXA || '',
				priority: item.Priority || '',
				reportDate: formatTrainingDate(item.ESTStudentReportDate),
				startDate: formatTrainingDate(item.ESTStartDate),
				endDate: formatTrainingDate(item.ESTEndDate),
				duration: [item.Duration, item.DurationType].filter(Boolean).join(' '),
				classId: item.TrainingClassID || '',
				tMasl: item.TMASL || '',
				status: item.TrainingStatus || '',
				financialStatus: item.FinancialStatus || '',
				facilityCategory: item.FincialCategory || '',
			}))
	} catch (error) {
		console.error('Failed to load training track lines:', error)
		return []
	}
}

export const getTrainingTrackDetailByGUID = async (trainingTrackGUID: string) => {
	try {
		const items = await getTrainingTrackItems()
		return (
			items.find(
				(item: any) =>
					item.trainingTrackGUID ===
					trainingTrackGUID
			) || null
		)
	} catch (error) {
		console.error('Failed to load training detail:', error)
		return null
	}
}
const GRANT_STATUS_LABEL: Record<number, string> = {
	1: 'Saved',
	2: 'Initiated',
	3: 'Canceled',
	4: 'Waiting for Internal Review',
	5: 'Waiting for DSCA Funding',
	6: 'Implemented',
	7: 'Active',
	8: 'Closed',
}

const getGrantTabKey = (statusCode: number): RequirementTabKey => {
	if (statusCode === 1) return 'saved'
	if (statusCode === 3) return 'canceled'
	return 'all'
}

export const mapGrantItem = (item: any) => {
	const statusCode = Number(item.StatusCode ?? item.statusCode ?? 1)
	const isSaved = statusCode === 1
	const eKey = item.eKey ?? ''
	return {
		requestID: item.CaseID ? String(item.CaseID).padStart(5, '0') : '',
		ItemGUID: item.CaseGUID ?? eKey,
		eKey,
		navPath: isSaved ? `grant/${item.CaseGUID ?? eKey}` : `detail-view/${item.CaseGUID ?? eKey}`,
		country: item.Country ?? '',
		ia: item.ImpAgencyID ?? '',
		programtype: shortProgramType(item.ProgramType ?? ''),
		fundingSource: item.FundingSource ?? '',
		caseType: item.CaseType ?? '',
		userCaseId: item.UserCaseID ?? '',
		caseDesignator: item.CaseDesignator ?? '',
		fiscalYear: item.FiscalYear ?? '',
		endFiscalYear: item.EndFiscalYear ?? '',
		submittedBy: item.ItemCreatedBy ?? '',
		ItemCreated: item.ItemCreated ?? '',
		submitteddate: item.ItemCreated
			? new Date(item.ItemCreated).toLocaleDateString()
			: '',
		status: GRANT_STATUS_LABEL[statusCode] ?? 'Unknown',
		StatusCode: item?.StatusCode,
		tabKey: getGrantTabKey(statusCode),
		Description: item.Description ?? '',
		CaseGUID: item.CaseGUID ?? '',
	}
}

const _siteSegment = window.location.href.split('/')[5] ?? window.location.hostname
const MASTER_STORE = `MasterList_${_siteSegment}`
const MASTER_KEY_PATH = 'ID'
const MASTER_INDEXED_COLS = ['ItemModified']

const isIndexedDBAvailable = async (): Promise<boolean> => {
	try {
		await dbStore(MASTER_STORE, MASTER_KEY_PATH, MASTER_INDEXED_COLS)
		return true
	} catch {
		return false
	}
}

export const fetchMasterListItems = async (): Promise<RequirementItem[]> => {
	// Check if IndexedDB is available in this browser/environment
	const idbAvailable = await isIndexedDBAvailable()

	if (!idbAvailable) {
		// IndexedDB blocked (enterprise policy, Edge restriction, etc.)
		// Fall back to direct RhyBus fetch every time
		const list = await rhybusRListsHttp().getList(ListNames.MASTERLIST)
		const items = [...(await list.query().get<I_Master>())].catch?.(() => []) ?? []
		return (Array.isArray(items) ? items : []).map(mapMasterItemToRequirementItem)
	}

	// 1. Load cached items from IndexedDB
	const cached: I_Master[] = await getAllItemsInStore(MASTER_STORE, MASTER_KEY_PATH).catch(() => [])

	// 2. Get latest modified date for delta fetch.
	//    If the index is missing (DB was created without it), delete & re-init so
	//    the next load will have the correct index and can do true delta fetches.
	const latestDate = await getLatestModifiedDate(MASTER_STORE, 'ItemModified', MASTER_KEY_PATH)
		.catch(async () => {
			await deleteIDB(MASTER_STORE).catch(() => { })
			await dbStore(MASTER_STORE, MASTER_KEY_PATH, MASTER_INDEXED_COLS).catch(() => { })
			return null
		})

	// 3. Fetch only items newer than the cached date (full fetch if no cache)
	const list = await rhybusRListsHttp().getList(ListNames.MASTERLIST)
	let query = list.query()
	if (latestDate) {
		query = query.filter(`ItemModified gt '${latestDate}'`)
	}
	const deltaItems: I_Master[] = [...(await query.get<I_Master>())].catch?.(() => []) ?? []
	const deltaArr = Array.isArray(deltaItems) ? deltaItems : []

	// 4. Save delta items to IndexedDB
	if (deltaArr.length > 0) {
		await saveItems(MASTER_STORE, deltaArr, MASTER_KEY_PATH, MASTER_INDEXED_COLS).catch((err: any) => {
			console.warn('IndexedDB save failed:', err?.message ?? err)
		})
	}

	// 5. Merge: delta overwrites cached items on same eKey
	const mergedMap = new Map<string, I_Master>()
	cached.forEach((item: I_Master) => mergedMap.set((item as any)?.eKey ?? '', item))
	deltaArr.forEach((item: I_Master) => mergedMap.set((item as any)?.eKey ?? '', item))

	const merged = Array.from(mergedMap.values())
	if (!merged.length) return []

	merged.sort((a: any, b: any) => new Date(b.ItemModified).valueOf() - new Date(a.ItemModified).valueOf())

	return merged.map(mapMasterItemToRequirementItem)
}
export const fetchGrantByGuid = async (guid: string): Promise<any | null> => {
	try {
		const list = await rhybusRListsHttp().getList(ListNames.GRANTLIST)
		const items = [...(await list.query().get<any>())]
		return items.find((item: any) => item.eKey === guid) ?? null
	} catch (err) {
		console.error('[fetchGrantByGuid]', err)
		return null
	}
}

export const fetchGrantListItems = async () => {
	try {
		const list = await rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER)
		const items = [...(await list.query().get<any>())]
		items.sort((a: any, b: any) =>
			new Date(b.ItemModified ?? b.ItemCreated ?? 0).valueOf() -
			new Date(a.ItemModified ?? a.ItemCreated ?? 0).valueOf()
		)
		return items.map(mapGrantItem)
	} catch (error) {
		console.error('Failed to fetch grants:', error)
		return []
	}
}

const PROGRAM_TYPE_COLORS = ['#8722E4', '#3148C6', '#6874A6', '#237F92', '#7A1FE5', '#807605', '#963411', '#2F1DBA', '#115696', '#5FA91F']

export const fetchProgramTypeChartData = async (): Promise<{ name: string; value: number; fill: string }[]> => {
	try {
		const list = await rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER)
		const allItems = [...(await list.query().get<any>())]
		const items = allItems.filter((item: any) => Number(item.StatusCode ?? 0) !== 1)
		const counts: Record<string, number> = {}
		items.forEach((item: any) => {
			const pt = item.ProgramType
			if (pt) counts[pt] = (counts[pt] || 0) + 1
		})
		return Object.entries(counts)
			.sort(([a], [b]) => a.localeCompare(b))
			.map(([fullName, value], i) => ({
				name: fullName.includes(' - ') ? fullName.split(' - ')[0].trim() : fullName,
				value,
				fill: PROGRAM_TYPE_COLORS[i % PROGRAM_TYPE_COLORS.length],
			}))
	} catch (error) {
		console.error('Failed to fetch program type chart data:', error)
		return []
	}
}

export const fetchImpAgencyChartData = async (): Promise<{ name: string; value: number }[]> => {
	try {
		const list = await rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER)
		const allItems = [...(await list.query().get<any>())]
		const items = allItems.filter((item: any) => Number(item.StatusCode ?? 0) !== 1)
		const counts: Record<string, number> = {}
		items.forEach((item: any) => {
			const agency = item.ImpAgencyID
			if (agency) counts[agency] = (counts[agency] || 0) + 1
		})
		return Object.entries(counts)
			.sort(([a], [b]) => a.localeCompare(b))
			.map(([name, value]) => ({ name, value }))
	} catch (error) {
		console.error('Failed to fetch implementing agency chart data:', error)
		return []
	}
}

export const fetchTrainingItemsForKpi = async (): Promise<{ caseGUID: string; statusCode: number }[]> => {
	try {
		const list = await rhybusRListsHttp().getList(ListNames.DSCA_TRAINING_TRACK)
		const items = [...(await list.query().get<any>())]
		return items
			.filter((item: any) => !item.IsDeleted && Number(item.StatusCode ?? 0) !== 1)
			.map((item: any) => ({
				caseGUID: item.CaseGUID ?? '',
				statusCode: Number(item.StatusCode ?? 0),
			}))
	} catch (error) {
		console.error('Failed to fetch training items for KPI:', error)
		return []
	}
}

export const fetchDashboardKpiCounts = async (): Promise<{
	totalCases: number
	totalTrainings: number
	activeTrainings: number
	completedTrainings: number
}> => {
	try {
		const [caseList, trainingList] = await Promise.all([
			rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER),
			rhybusRListsHttp().getList(ListNames.DSCA_TRAINING_TRACK),
		])
		const [allCaseItems, allTrainingItems] = await Promise.all([
			caseList.query().get<any>().then((r: any) => [...r]),
			trainingList.query().get<any>().then((r: any) => [...r]),
		])
		const caseItems = allCaseItems.filter((item: any) => Number(item.StatusCode ?? 0) !== 1)
		const trainingItems = allTrainingItems.filter((item: any) => Number(item.StatusCode ?? 0) !== 1)
		const totalCases = caseItems.length
		const totalTrainings = trainingItems.length
		const activeTrainings = trainingItems.filter((item: any) => {
			const sc = Number(item.StatusCode ?? 0)
			return sc !== 3
		}).length
		const completedTrainings = trainingItems.filter((item: any) => {
			const sc = Number(item.StatusCode ?? 0)
			return sc === 8
		}).length
		return { totalCases, totalTrainings, activeTrainings, completedTrainings }
	} catch (error) {
		console.error('Failed to fetch dashboard KPI counts:', error)
		return { totalCases: 0, totalTrainings: 0, activeTrainings: 0, completedTrainings: 0 }
	}
}

const shortProgramType = (fullName: string) =>
	fullName.includes(' - ') ? fullName.split(' - ')[0].trim() : fullName

export const fetchHomeCaseItems = async (): Promise<HomeNewRequirementItem[]> => {
	try {
		const list = await rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER)
		const allItems = [...(await list.query().get<any>())]
		return allItems
			.filter((item: any) => Number(item.StatusCode ?? 0) !== 1 && Number(item.StatusCode ?? 0) !== 3)
			.map((item: any, i: number): HomeNewRequirementItem => ({
				ID: item.CaseID ?? i,
				ItemGuid: item.CaseGUID ?? '',
				requestID: item.UserCaseID ?? '',
				fiscalYear: String(item.FiscalYear ?? ''),
				ia: item.ImpAgencyID ?? '',
				programtype: shortProgramType(item.ProgramType ?? ''),
				trainingType: '',
				caseType: item.CaseType ?? '',
				country: item.Country ?? '',
				organization: item.ImpAgencyID ?? '',
				status: item.StatusTitle ?? '',
				submittedDate: item.ItemCreated ? new Date(item.ItemCreated).toLocaleDateString() : '',
				ceilingUSD: '',
			}))
	} catch (error) {
		console.error('Failed to fetch home case items:', error)
		return []
	}
}

export const fetchCountryChartData = async (): Promise<{ name: string; value: number }[]> => {
	try {
		const list = await rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER)
		const allItems = [...(await list.query().get<any>())]
		const items = allItems.filter((item: any) => Number(item.StatusCode ?? 0) !== 1)
		const counts: Record<string, number> = {}
		items.forEach((item: any) => {
			const country = item.Country
			if (country) counts[country] = (counts[country] || 0) + 1
		})
		return Object.entries(counts)
			.sort(([a], [b]) => a.localeCompare(b))
			.map(([name, value]) => ({ name, value }))
	} catch (error) {
		console.error('Failed to fetch country chart data:', error)
		return []
	}
}

export const getRequirementKpiGroups = (items: RequirementItem[]) => {
	const fundedItems = items.filter(item => parseFloat(item.fundedAmount || '0') > 0)
	const unfundedItems = items.filter(item => parseFloat(item.unfundedAmount || '0') > 0)

	return [
		{
			title: '',
			collapsible: false,
			isGroup: false,
			gridcolumncount: 'gridcolumn-2',
			items: KPI_STATUS_LIST.map(s => ({
				label: s.label,
				value: items.filter(item => item.status === s.label).length,
				color: s.color
			}))
		},
		{
			title: 'Active',
			isGroup: true,
			headerMeta: {
				color: '16',
				count: fundedItems.length,
				value: fundedItems.length,
				amount: 0,
				layout: {
					count: 'right',
					amount: '',
				},
			}
		},
		{
			title: 'Closure',
			isGroup: true,
			headerMeta: {
				color: '31',
				count: unfundedItems.length,
				value: unfundedItems.length,
				amount: 0,
				layout: {
					count: 'right',
					amount: '',
				},
			}
		}
	]
}

export const getTrainingKpiGroups = (items: TrainingFilterItem[]) => {
	return [
		{
			title: '',
			collapsible: false,
			isGroup: false,
			gridcolumncount: 'gridcolumn-2',
			items: TRAINING_KPI_STATUS_LIST.map(s => ({
				label: s.label,
				value: items.filter(item => String(item['status'] ?? '') === s.label).length,
				color: s.color
			}))
		}

	]
}

// ============================================================================
// KPI FILTER LOGIC
// ============================================================================

/**
 * Gets the filter predicate function for a given KPI key
 * 
 * @param kpiKey - The KPI identifier (numeric color code or status label)
 * @returns A filter function that takes a RequirementItem and returns boolean
 * 
 * @example
 * const filterFn = getKpiFilterPredicate('1')
 * const filtered = items.filter(filterFn) // Returns items with status = 'Initiator'
 */
export const getKpiFilterPredicate = (kpiLabel: string): ((item: RequirementItem) => boolean) => {
	const normalizedLabel = kpiLabel.trim()

	// Check if it's a status KPI
	const isStatus = KPI_STATUS_LIST.some(s => s.label === normalizedLabel)
	if (isStatus) {
		return (item: RequirementItem) => item.status === normalizedLabel
	}

	// Check if it's a QA/QC branch KPI
	const isBranch = KPI_QAQC_BRANCHES.some(b => b.label === normalizedLabel)
	if (isBranch) {
		return (item: RequirementItem) => item.status === 'Waiting for QA/QC' && item.branch === normalizedLabel
	}

	// Funded / Unfunded
	if (normalizedLabel === 'Funded') {
		return (item: RequirementItem) => parseFloat(item.fundedAmount || '0') > 0
	}
	if (normalizedLabel === 'Unfunded') {
		return (item: RequirementItem) => parseFloat(item.unfundedAmount || '0') > 0
	}

	// Fallback: try direct status match
	const lower = normalizedLabel.toLowerCase()
	return (item: RequirementItem) =>
		item.status.toLowerCase() === lower ||
		item.tabKey === (lower as RequirementTabKey)
}

/**
 * Applies KPI filter to requirement items based on clicked KPI item
 * 
 * @param items - The complete list of requirement items
 * @param item - The clicked KPI item object with { label, value, color }
 * @returns Filtered array of RequirementItem matching the KPI criteria
 * 
 * @example
 * const filteredItems = applyKpiFilter(allItems, { label: 'Initiator', value: 5, color: '1' })
 * // Returns items with status = 'Initiator'
 */
export const applyKpiFilter = (items: RequirementItem[], item: any): RequirementItem[] => {
	try {
		const kpiLabel = item?.label || item?.title

		if (!kpiLabel) {
			return items
		}

		const filterPredicate = getKpiFilterPredicate(kpiLabel)

		// Apply filter to all items
		const filteredResult = items.filter(filterPredicate)

		if (!filteredResult || filteredResult.length === 0) {
			return []
		}

		return filteredResult
	} catch (error) {
		console.error('Error applying KPI filter:', error)
		return items
	}
}

export const buildInitialFilters = (config: any[]): FilterState => {
	const initial: FilterState = {}

	config.forEach((c: any, idx: number) => {
		const realOptions = (c.options ?? []).filter((o: any) => o.value !== 'All')
		const autoSelect = realOptions.length === 1
			? realOptions[0]
			: { label: c.defaultValue ?? 'All', value: c.defaultValue ?? 'All' }

		initial[c.key] = {
			order: idx + 1,
			value: [autoSelect]
		}
	})

	return initial
}

export const getActiveTabKey = (tabIndex: number): RequirementTabKey => {
	if (tabIndex === 1) return 'canceled'
	if (tabIndex === 2) return 'saved'
	return 'all'
}

export const computeAutoSelectFilterUpdates = (
	cascadingFilterConfig: any[],
	filters: FilterState
): FilterState | null => {
	const updates: FilterState = {}
	let hasUpdate = false

	cascadingFilterConfig.forEach((config: any, idx: number) => {
		const realOptions = config.options.filter((o: any) => o.value !== 'All')
		if (realOptions.length === 1) {
			const currentVal = filters[config.key]?.value
			const alreadySelected =
				currentVal?.length === 1 && currentVal[0]?.value === realOptions[0].value
			if (!alreadySelected) {
				updates[config.key] = { order: idx + 1, value: [realOptions[0]] }
				hasUpdate = true
			}
		}
	})

	return hasUpdate ? updates : null
}

export const mergeExportColumnSelections = (savedColumns: {
	available: ListViewExportColumn[]
	selected: ListViewExportColumn[]
}) => {
	const selectedColumns = savedColumns.selected.map(column => ({ ...column, IsSelected: true }))
	const availableColumns = savedColumns.available.map(column => ({ ...column, IsSelected: false }))
	return {
		allColumns: [...selectedColumns, ...availableColumns],
		selectedColumns,
		availableColumns
	}
}

export const buildResetColumns = (
	configuredColumns: ListViewExportColumn[],
	fallbackColumns: ListViewExportColumn[]
): ListViewExportColumn[] => {
	return (configuredColumns.length > 0 ? configuredColumns : fallbackColumns).map(column => ({
		...column,
		IsSelected: column.IsSelected !== false
	}))
}

export const validateTableColumnSelection = (columns: any[], minCount = 2): string => {
	if (columns.length < minCount) {
		return `At least ${minCount} columns must be selected.`
	}
	return ''
}
